import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useState } from "react";

import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";

import { useAppSelector } from "app/hooks";
import CircularProgress from "components/circular-progress";
import MaybeMentionsCommentHint from "components/mentions/launch/mentions-comment-hint.component";
import MentionSaveHint from "components/mentions/mention-save-hint.component";
import MentionTextField from "components/mentions/mention-text-field.component";
import {
  type MentionPick,
  getMentionedIdsInText,
  getMentionedTeamIdsInText
} from "components/mentions/mention-text.util";
import { TEAMS_FEATURE, TEAM_MENTIONS_FEATURE } from "constants/feature-flags";
import {
  selectCurrentClinic,
  selectCurrentUser
} from "features/auth/auth-slice";
import { useCreateStaffTaskCommentMutation } from "services/staff-tasks";

type AddUserCommentProps = {
  taskId: number;
  isBusy?: boolean;
};

export default function AddUserComment({
  taskId,
  isBusy
}: AddUserCommentProps) {
  const currentUser = useAppSelector(selectCurrentUser);
  const currentClinicId = useAppSelector(selectCurrentClinic)?.id;
  const flags = useFlags();
  const isMentionsEnabled = Boolean(flags[TEAM_MENTIONS_FEATURE]);
  const isTeamsEnabled = isMentionsEnabled && Boolean(flags[TEAMS_FEATURE]);
  const [createComment, { isLoading }] = useCreateStaffTaskCommentMutation();

  const [value, setValue] = useState("");
  const [mentions, setMentions] = useState<MentionPick[]>([]);

  useEffect(() => {
    setValue("");
    setMentions([]);
  }, [taskId]);

  const handleCreate = () => {
    createComment({
      taskId,
      text: value,
      user: currentUser.id,
      // Write-only key, sent only with the flag on: an older backend 400s it.
      ...(isMentionsEnabled
        ? { mentioned_user_ids: getMentionedIdsInText(value, mentions) }
        : {}),
      // Teams picked in the @ list (TEAMS_FEATURE): another unknown key on an older backend.
      ...(isTeamsEnabled
        ? { mentioned_team_ids: getMentionedTeamIdsInText(value, mentions) }
        : {})
    }).then(() => {
      setValue("");
      setMentions([]);
    });
  };

  const onKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (event) => {
    if (event.key === "Enter" && event.ctrlKey) {
      event.preventDefault();
      event.stopPropagation();
      handleCreate();
    }
  };

  return (
    <>
      <Stack direction="row" width="100%" columnGap={2} alignItems="center">
        <Stack flex={2.5} justifyContent="flex-end" width="100%" mt={6}>
          <Box
            id="test"
            display="flex"
            sx={{
              mr: 2,
              mb: 2,
              width: "1px",
              background: grey[400],
              height: "30px",
              alignSelf: "flex-end"
            }}
          ></Box>
          <Stack direction="row" width="100%" columnGap={1}>
            <Stack display="flex" flex={5} justifyContent="center">
              <Typography
                variant="body1"
                sx={{
                  mt: 0.5,
                  textAlign: "right",
                  color: grey[700],
                  fontSize: "13px",
                  lineHeight: 1.2
                }}
              >
                {currentUser.fullName}
              </Typography>
            </Stack>
            <Box display="flex">
              <Avatar
                alt="avatar"
                src={currentUser.photo ?? ""}
                sx={{
                  border: `solid 1px ${grey[400]}`,
                  width: "32px",
                  height: "32px"
                }}
                slotProps={{ img: { loading: "lazy" } }}
              />
            </Box>
          </Stack>
        </Stack>
        <Stack flex={5} alignSelf="flex-end" mt={2}>
          <Stack direction="row">
            {isBusy || isLoading ? (
              <CircularProgress sx={{ py: 0 }} />
            ) : (
              <MaybeMentionsCommentHint>
                <MentionTextField
                  enabled={isMentionsEnabled}
                  clinicId={currentClinicId}
                  includeTeams={isTeamsEnabled}
                  mentions={mentions}
                  onMentionsChange={setMentions}
                  value={value}
                  onChange={setValue}
                  placeholder="Enter your comment..."
                  maxRows={3}
                  autoComplete="off"
                  InputProps={{
                    readOnly: isLoading
                  }}
                  inputProps={
                    isMentionsEnabled ? { "aria-label": "Comment" } : undefined
                  }
                  sx={{
                    width: "100%"
                  }}
                  onKeyDown={onKeyDown}
                />
              </MaybeMentionsCommentHint>
            )}

            <Box width="42px"></Box>
          </Stack>
          {isMentionsEnabled && (
            // Same right inset as the field (the 42px spacer above), so the open @ list covers it.
            <Box pr="42px">
              <MentionSaveHint action="add the comment" />
            </Box>
          )}
          <Button
            sx={{ mt: 1, width: "65px" }}
            style={{ padding: "4px" }}
            size="small"
            variant="outlined"
            disabled={isLoading || isBusy}
            onClick={handleCreate}
          >
            Add
          </Button>
        </Stack>
      </Stack>
    </>
  );
}
